import { IGameSocketNotifyMessage, IGameSocketNotifyMessageNotificationData } from "../../../src/models/game/socket";
import { GameNotificationBlock, GameNotificationBlockType, GameNotificationGameBlock } from "../../../src/server/models/game/modules/notifications/blocks";
import styles from './notifications.module.css';
import gameStyles from '../../game/gameslist/gameslistgame.module.css';
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import NotificationTranslation from "./notificationtranslation";
import { getMinimumRelativeTimeString } from "../../../src/util/relativetime";

type Props = {
    message: IGameSocketNotifyMessage;
    onClose?: () => void;
};

export default function NotificationComponent(
    {
        message,
        onClose,
    }: Props,
): React.ReactNode {
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const router = useRouter();

    const data: IGameSocketNotifyMessageNotificationData = message.data;
    const blocks: Record<string, GameNotificationBlock> = data.blocks;
    const blocksValues = Object.values(blocks);
    const gameBlock = blocksValues.find((block) => block.type === GameNotificationBlockType.Game) as GameNotificationGameBlock;
    const title = t(data.titleKey, data.titleValues);

    const onClick = useCallback(
        () => {
            router.push(`/game/play?id=${message.gameId}`);

            if (onClose != null) {
                onClose();
            }
        },
        [router, message.gameId, onClose],
    );

    // unfortunately, blueprint doesn't let me add a proper link
    // to a toast so I have to hack it this way
    //
    // adding onClick to the current component would ignore the
    // padding area
    useEffect(
        () => {
            if (containerRef.current) {
                const parent = containerRef.current.parentElement;

                parent.onclick = onClick;
                parent.style.cursor = 'pointer';
            }
        },
        [onClick],
    );

    const timeString = useMemo(
        (): string => {
            if (!message.ts) {
                return '';
            }

            return `(${getMinimumRelativeTimeString(message.ts, Date.now())} ago)`;
        },
        [message],
    );

    return <div className={styles.notificationContainer} ref={containerRef}>
        <div>
            <picture>
                <img
                    alt={gameBlock.game.map.name}
                    src={`/api/map/getthumbnail?mapId=${gameBlock.game.map.id}`}
                    draggable={false}
                    className={[gameStyles.mapImage, styles.map].join(' ')}
                />
            </picture>
        </div>
        <div>
            <div className={styles.title}>
                <div className={styles.titleText}>
                    {title}
                </div>
                <div className={styles.time}>
                    {timeString}
                </div>
            </div>
            <div>
                <NotificationTranslation
                    translationKey={data.key}
                    blocks={blocks}
                />
            </div>
        </div>
    </div>
}
